/**
 * AddContactInline — small "save new contact" form embedded inside the
 * proposal contact picker. Saves the contact back to the client (and
 * optionally to the site) and returns it to the caller so the picker
 * can pre-select the freshly created contact.
 *
 * Extracted from `pages/Proposals.jsx` to slim the file (was 2,302 lines
 * before — this extract drops it by ~40 lines).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { addClientContact } from "@/lib/api";

export function AddContactInline({ clientId, siteId, onAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!clientId) { toast.error("Select a customer first"); return; }
    if (!email.trim()) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      const res = await addClientContact(
        clientId,
        { name: name.trim() || email.trim(), email: email.trim(), phone: phone.trim() },
        siteId,
      );
      const contact = res.data?.contact || res.data || { name: name.trim() || email.trim(), email: email.trim(), phone: phone.trim() };
      toast.success(siteId ? "Contact saved to customer & site" : "Contact saved to customer");
      setName(""); setEmail(""); setPhone("");
      onAdded(contact);
    } catch {
      toast.error("Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-md p-3 bg-slate-50 space-y-2" data-testid="add-contact-inline">
      <Label className="text-xs font-semibold text-slate-700">Add New Contact (saves to customer)</Label>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Name" value={name} onChange={e => setName(e.target.value)} className="h-8 text-sm" data-testid="add-contact-name" />
        <Input placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)} className="h-8 text-sm" data-testid="add-contact-phone" />
      </div>
      <Input type="email" placeholder="email@example.com *" value={email} onChange={e => setEmail(e.target.value)} className="h-8 text-sm" data-testid="add-contact-email" />
      <Button type="button" size="sm" onClick={save} disabled={saving} data-testid="add-contact-save" className="bg-slate-900 hover:bg-slate-800 text-white">
        <UserPlus className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving..." : "Save Contact"}
      </Button>
    </div>
  );
}
